import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";

export default function PkLauncher({ sessionId, open, onClose }) {
  const [uname, setUname] = useState("");
  const [busy, setBusy] = useState("");
  const [queued, setQueued] = useState(false);
  const [incoming, setIncoming] = useState([]);
  const [current, setCurrent] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const [inc, cur] = await Promise.all([
        api.get("/live/pk/incoming").then((r) => r.data).catch(() => []),
        api.get(`/live/${sessionId}/pk`).then((r) => r.data).catch(() => ({ pk: null })),
      ]);
      setIncoming(inc || []);
      setCurrent(cur?.pk || null);
      if (cur?.pk?.status === "active") setQueued(false);
    } catch { /* ignore */ }
  }, [sessionId]);

  useEffect(() => {
    if (!open) return;
    refresh();
    const t = setInterval(refresh, 2500);
    return () => clearInterval(t);
  }, [open, refresh]);

  if (!open) return null;

  const invite = async () => {
    const u = uname.trim().replace(/^@/, "");
    if (!u) return;
    setBusy("invite");
    try { await api.post(`/live/${sessionId}/pk/invite`, { opponent_username: u }); toast.success(`Invite sent to @${u} ⚔️`); setUname(""); refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't send invite"); }
    finally { setBusy(""); }
  };
  const quick = async () => {
    setBusy("quick");
    try {
      const { data } = await api.post(`/live/${sessionId}/pk/quickmatch`, {});
      if (data.matched) { toast.success("Matched! ⚔️"); onClose(); }
      else { setQueued(true); toast.message("Searching for an opponent…"); }
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Quick Match failed"); }
    finally { setBusy(""); }
  };
  const cancelQuick = async () => { try { await api.post(`/live/${sessionId}/pk/quickmatch/cancel`, {}); setQueued(false); } catch { /* ignore */ } };
  const accept = async (id) => { setBusy(id); try { await api.post(`/live/pk/${id}/accept`, {}); toast.success("PK accepted! ⚔️"); onClose(); } catch (e) { toast.error(e.response?.data?.detail || "Couldn't accept"); } finally { setBusy(""); refresh(); } };
  const decline = async (id) => { setBusy(id); try { await api.post(`/live/pk/${id}/decline`, {}); } catch { /* ignore */ } finally { setBusy(""); refresh(); } };
  const endPk = async () => { if (!current) return; setBusy("end"); try { await api.post(`/live/pk/${current.id}/end`, {}); onClose(); } catch { /* ignore */ } finally { setBusy(""); } };
  const activeOrInvited = current && (current.status === "active" || current.status === "invited");

  return (
    <div className="absolute inset-0 z-50 bg-ink/80 backdrop-blur-sm flex items-end" onClick={onClose}>
      <div className="w-full bg-ink rounded-t-3xl p-5 border-t border-white/10" onClick={(e) => e.stopPropagation()} data-testid="pk-launcher">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-cream text-2xl font-black tracking-tighter">⚔️ PK Battle</h3>
          <button onClick={onClose} className="text-cream/60 text-xs tracking-[0.2em] uppercase">Close</button>
        </div>
        {activeOrInvited ? (
          <div className="rounded-2xl bg-white/5 border border-white/10 p-5 text-center">
            <p className="text-cream font-black">{current.status === "active" ? "PK in progress" : "Invite pending"} vs @{current.my_side === "a" ? current.host_b?.username : current.host_a?.username}</p>
            {current.status === "active" ? <button onClick={endPk} data-testid="pk-launcher-end" className="mt-3 px-6 py-2.5 rounded-full bg-red-500 text-white text-xs font-black">End PK</button> : null}
          </div>
        ) : (
          <>
            <span className="text-cream/60 text-[10px] tracking-[0.2em] uppercase font-bold">Invite a creator</span>
            <div className="flex gap-2 mt-1 mb-4">
              <input data-testid="pk-invite-input" value={uname} onChange={(e) => setUname(e.target.value)} placeholder="@username" className="flex-1 bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-cream text-sm focus:outline-none focus:border-papaya" />
              <button onClick={invite} disabled={busy === "invite"} data-testid="pk-invite-send" className="px-5 rounded-2xl bg-papaya text-ink font-black text-sm disabled:opacity-50">Invite</button>
            </div>
            <span className="text-cream/60 text-[10px] tracking-[0.2em] uppercase font-bold">Or find one instantly</span>
            {queued ? (
              <button onClick={cancelQuick} data-testid="pk-quick-cancel" className="mt-1 w-full py-3 rounded-full bg-papaya/15 border border-papaya text-papaya font-black text-sm">Searching… tap to cancel</button>
            ) : (
              <button onClick={quick} disabled={busy === "quick"} data-testid="pk-quick-match" className="mt-1 w-full py-3 rounded-full bg-white/10 border border-white/15 text-cream font-black text-sm disabled:opacity-50">⚡ Quick Match</button>
            )}
          </>
        )}
        {incoming.length > 0 ? (
          <div className="mt-4">
            <span className="text-cream/60 text-[10px] tracking-[0.2em] uppercase font-bold">Incoming challenges</span>
            <div className="space-y-2 mt-1">
              {incoming.map((p) => (
                <div key={p.id} data-testid={`pk-incoming-${p.id}`} className="flex items-center justify-between rounded-2xl bg-white/5 border border-white/10 p-3">
                  <span className="text-cream font-bold truncate">@{p.host_a?.username}</span>
                  <div className="flex gap-2">
                    <button onClick={() => decline(p.id)} data-testid="pk-decline" className="px-3 py-1.5 rounded-full bg-white/10 text-cream/70 text-xs font-black">Decline</button>
                    <button onClick={() => accept(p.id)} data-testid="pk-accept" className="px-4 py-1.5 rounded-full bg-papaya text-ink text-xs font-black">Accept</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
